import { generateRandomNumber } from '../../../../../utils/utils';
import BasePlugin, {type BasePluginEvents} from '../base-plugin';
import createElement from '../dom';
import EventEmitter from '../event-emitter';
import {effect} from '../reactive/arr-waveform-store';
import {createDragStream} from '../reactive/drag-stream';
export type RegionsPluginOptions = undefined;
export type UpdateSide = 'start' | 'end';
export type RegionsPluginEvents = BasePluginEvents & {
  'region-initialized': [region: Region];
  'region-created': [region: Region];
  'region-update': [region: Region, side?: UpdateSide];
  'region-updated': [region: Region, side?: UpdateSide];
  'region-removed': [region: Region];
  'region-clicked': [region: Region, e: MouseEvent];
  'region-double-clicked': [region: Region, e: MouseEvent];
  'region-in': [region: Region];
  'region-out': [region: Region];
  'region-content-changed': [region: Region];
};
export type RegionEvents = {
  remove: [];
  update: [side?: UpdateSide];
  'update-end': [side?: UpdateSide];
  play: [end?: number];
  click: [event: MouseEvent];
  dblclick: [event: MouseEvent];
  over: [event: MouseEvent];
  leave: [event: MouseEvent];
  'content-changed': [];
};
export type RegionParams = {
  id?: string;
  start: number;
  end?: number;
  drag?: boolean;
  resize?: boolean;
  resizeStart?: boolean;
  resizeEnd?: boolean;
  color?: string;
  content?: string | HTMLElement;
  minLength?: number;
  maxLength?: number;
  channelIdx?: number;
  contentEditable?: boolean;
};
class SingleRegion extends EventEmitter<RegionEvents> implements Region {
  public element: HTMLElement | null = null;
  public id: string;
  public start: number;
  public end: number;
  public drag: boolean;
  public resize: boolean;
  public resizeStart: boolean;
  public resizeEnd: boolean;
  public color: string;
  public content?: HTMLElement;
  public minLength = 0;
  public maxLength = Infinity;
  public channelIdx: number;
  public contentEditable = false;
  public subscriptions: (() => void)[] = [];
  public updatingSide?: UpdateSide = undefined;
  private isRemoved = false;
  private contentClickListener?: (e: MouseEvent) => void;
  private contentBlurListener?: () => void;
  constructor(
    params: RegionParams,
    private totalDuration: number,
    private numberOfChannels = 0
  ) {
    super();
    this.subscriptions = [];
    this.id = params.id || `region-${generateRandomNumber().toString(32).slice(2)}`;
    this.start = this.clampPosition(params.start);
    this.end = this.clampPosition(params.end ?? params.start);
    this.drag = params.drag ?? true;
    this.resize = params.resize ?? true;
    this.resizeStart = params.resizeStart ?? true;
    this.resizeEnd = params.resizeEnd ?? true;
    this.color = params.color ?? 'rgba(0, 0, 0, 0.1)';
    this.minLength = params.minLength ?? this.minLength;
    this.maxLength = params.maxLength ?? this.maxLength;
    this.channelIdx = params.channelIdx ?? -1;
    this.contentEditable = params.contentEditable ?? this.contentEditable;
    this.element = this.initElement();
    this.setContent(params.content);
    this.setPart();
    this.renderPosition();
    this.initMouseEvents();
  }
  private clampPosition(time: number): number {
    return Math.max(0, Math.min(this.totalDuration, time));
  }
  private setPart() {
    this.element?.setAttribute(
      'part',
      `${this.isMarker() ? 'marker' : 'region'} ${this.id}`
    );
  }
  private addResizeHandles(element: HTMLElement) {
    const handleStyle = {
      position: 'absolute',
      zIndex: '2',
      width: '6px',
      height: '100%',
      top: '0',
      cursor: 'ew-resize',
      wordBreak: 'keep-all',
    };
    const leftHandle = createElement(
      'div',
      {
        part: 'region-handle region-handle-left',
        style: {
          ...handleStyle,
          left: '0',
          borderLeft: '2px solid rgba(0, 0, 0, 0.5)',
          borderRadius: '2px 0 0 2px',
        },
      },
      element
    );
    const rightHandle = createElement(
      'div',
      {
        part: 'region-handle region-handle-right',
        style: {
          ...handleStyle,
          right: '0',
          borderRight: '2px solid rgba(0, 0, 0, 0.5)',
          borderRadius: '0 2px 2px 0',
        },
      },
      element
    );
    const resizeThreshold = 1;
    const leftDragStream = createDragStream(leftHandle, {
      threshold: resizeThreshold,
    });
    const rightDragStream = createDragStream(rightHandle, {
      threshold: resizeThreshold,
    });
    const unsubscribeLeft = effect(() => {
      const drag = leftDragStream.signal.value;
      if (!drag) return;
      if (drag.type === 'move' && drag.deltaX !== undefined) {
        this.onResize(drag.deltaX, 'start');
      } else if (drag.type === 'end') {
        this.onEndResizing('start');
      }
    }, [leftDragStream.signal]);
    const unsubscribeRight = effect(() => {
      const drag = rightDragStream.signal.value;
      if (!drag) return;
      if (drag.type === 'move' && drag.deltaX !== undefined) {
        this.onResize(drag.deltaX, 'end');
      } else if (drag.type === 'end') {
        this.onEndResizing('end');
      }
    }, [rightDragStream.signal]);
    this.subscriptions.push(() => {
      unsubscribeLeft();
      unsubscribeRight();
      leftDragStream.cleanup();
      rightDragStream.cleanup();
    });
  }
  private removeResizeHandles(element: HTMLElement) {
    const leftHandle = element.querySelector('[part*="region-handle-left"]');
    const rightHandle = element.querySelector('[part*="region-handle-right"]');
    if (leftHandle) {
      element.removeChild(leftHandle);
    }
    if (rightHandle) {
      element.removeChild(rightHandle);
    }
  }

  private getMarkerBGasBlobString(bgcolor: string): string {
    const svg = `<svg width="20" height="14" viewBox="0 0 20 14" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_3525_62144)">
    <path d="M10 0L20 16H0L10 0Z" fill="${bgcolor}" />
    <path opacity="0.8" d="M18.1963 15H1.80371L10 1.88574L18.1963 15Z" stroke="#FCFCFD" stroke-width="2" />
  </g>
  <defs>
    <clipPath id="clip0_3525_62144">
      <rect width="20" height="14" fill="white" />
    </clipPath>
  </defs>
</svg>`;
    const blob = new Blob([svg], {type: 'image/svg+xml'});
    const objectUrl = URL.createObjectURL(blob);
    return objectUrl;
  }

  private isMarker(): boolean {
    return this.start === this.end || !this.end;
  }

  private initElement(): HTMLElement | null {
    if (this.isRemoved) return null;
    let elementTop = 0;
    let elementHeight = 100;
    if (
      this.channelIdx >= 0 &&
      this.numberOfChannels > 0 &&
      this.channelIdx < this.numberOfChannels
    ) {
      elementHeight = 100 / this.numberOfChannels;
      elementTop = elementHeight * this.channelIdx;
    }
    let element;

    if (this.isMarker()) {
      element = createElement('div', {
        style: {
          zIndex: '5',
          position: 'absolute',
          bottom: `${0}px`,
          height: `${14}px`,
          width: `${20}px`,
          transform: `translateX(-50%)`,
          cursor: 'pointer',
          pointerEvents: 'none',
          backgroundSize: '100% 100%',
          isolation: 'isolate',
          backgroundImage: `url("${this.getMarkerBGasBlobString(this.color)}")`,
        },
      });
    } else {
      element = createElement('div', {
        style: {
          zIndex: '4',
          position: 'absolute',
          top: `${elementTop}%`,
          height: `${elementHeight}%`,
          backgroundColor: this.color,
          borderLeft: 'none',
          borderRadius: '2px',
          boxSizing: 'border-box',
          transition: 'background-color 0.2s ease',
          cursor: this.drag ? 'grab' : 'default',
          pointerEvents: 'none',
        },
      });
    }

    if (!this.isMarker() && this.resize) {
      this.addResizeHandles(element);
    }
    return element;
  }
  private renderPosition() {
    if (!this.element) return;
    const start = this.start / this.totalDuration;
    const end = (this.totalDuration - this.end) / this.totalDuration;
    this.element.style.left = `${start * 100}%`;
    this.element.style.right = `${end * 100}%`;
  }
  private toggleCursor(toggle: boolean) {
    if (!this.drag || !this.element?.style) return;
    this.element.style.cursor = toggle ? 'grabbing' : 'grab';
  }
  private initMouseEvents() {
    const {element} = this;
    if (!element) return;
    element.addEventListener('click', (e) => this.emit('click', e));
    element.addEventListener('mouseenter', (e) => this.emit('over', e));
    element.addEventListener('mouseleave', (e) => this.emit('leave', e));
    element.addEventListener('dblclick', (e) => this.emit('dblclick', e));
    element.addEventListener('pointerdown', () => this.toggleCursor(true));
    element.addEventListener('pointerup', () => this.toggleCursor(false));
    const dragStream = createDragStream(element);
    const unsubscribeDrag = effect(() => {
      const drag = dragStream.signal.value;
      if (!drag) return;
      if (drag.type === 'start') {
        this.toggleCursor(true);
      } else if (drag.type === 'move' && drag.deltaX !== undefined) {
        this.onMove(drag.deltaX);
      } else if (drag.type === 'end') {
        this.toggleCursor(false);
        if (this.drag) this.emit('update-end');
      }
    }, [dragStream.signal]);
    this.subscriptions.push(() => {
      unsubscribeDrag();
      dragStream.cleanup();
    });
    if (this.contentEditable && this.content) {
      this.contentClickListener = (e) => this.onContentClick(e);
      this.contentBlurListener = () => this.onContentBlur();
      this.content.addEventListener('click', this.contentClickListener);
      this.content.addEventListener('blur', this.contentBlurListener);
    }
  }
  public _onUpdate(dx: number, side?: UpdateSide, startTime?: number) {
    if (!this.element?.parentElement) return;
    const {width} = this.element.parentElement.getBoundingClientRect();
    const deltaSeconds = (dx / width) * this.totalDuration;
    let newStart =
      !side || side === 'start' ? this.start + deltaSeconds : this.start;
    let newEnd = !side || side === 'end' ? this.end + deltaSeconds : this.end;
    const isRegionCreating = startTime !== undefined;
    if (isRegionCreating) {
      if (this.updatingSide && this.updatingSide !== side) {
        if (this.updatingSide === 'start') {
          newStart = startTime;
        } else {
          newEnd = startTime;
        }
      }
    }
    newStart = Math.max(0, newStart);
    newEnd = Math.min(this.totalDuration, newEnd);
    const length = newEnd - newStart;
    this.updatingSide = side;
    const resizeValid = length >= this.minLength && length <= this.maxLength;
    if (newStart <= newEnd && (resizeValid || isRegionCreating)) {
      this.start = newStart;
      this.end = newEnd;
      this.renderPosition();
      this.emit('update', side);
    }
  }
  private onMove(dx: number) {
    if (!this.drag) return;
    this._onUpdate(dx);
  }
  private onResize(dx: number, side: UpdateSide) {
    if (!this.resize) return;
    if (!this.resizeStart && side === 'start') return;
    if (!this.resizeEnd && side === 'end') return;
    this._onUpdate(dx, side);
  }
  private onEndResizing(side: UpdateSide) {
    if (!this.resize) return;
    this.emit('update-end', side);
    this.updatingSide = undefined;
  }
  private onContentClick(event: MouseEvent) {
    event.stopPropagation();
    const contentContainer = event.target as HTMLDivElement;
    contentContainer.focus();
    this.emit('click', event);
  }
  public onContentBlur() {
    this.emit('update-end');
  }
  public _setTotalDuration(totalDuration: number) {
    this.totalDuration = totalDuration;
    this.renderPosition();
  }
  public play(stopAtEnd?: boolean) {
    this.emit(
      'play',
      stopAtEnd && this.end !== this.start ? this.end : undefined
    );
  }

  public getContent(asHTML: boolean = false): string | HTMLElement | undefined {
    if (asHTML) {
      return this.content || undefined;
    }
    if (this.element instanceof HTMLElement) {
      return this.content?.innerHTML || undefined;
    }
    return '';
  }
  public setContent(content: RegionParams['content']) {
    if (!this.element) return;
    if (this.content && this.contentEditable) {
      if (this.contentClickListener) {
        this.content.removeEventListener('click', this.contentClickListener);
      }
      if (this.contentBlurListener) {
        this.content.removeEventListener('blur', this.contentBlurListener);
      }
    }
    this.content?.remove();
    if (!content) {
      this.content = undefined;
      return;
    }
    if (typeof content === 'string') {
      this.content = createElement('div', {
        style: {
          padding: `0.2em ${this.isMarker() ? 0.2 : 0.4}em`,
          display: 'inline-block',
        },
        textContent: content,
      });
    } else {
      this.content = content;
    }
    if (this.contentEditable) {
      this.content.contentEditable = 'true';
      this.contentClickListener = (e) => this.onContentClick(e);
      this.contentBlurListener = () => this.onContentBlur();
      this.content.addEventListener('click', this.contentClickListener);
      this.content.addEventListener('blur', this.contentBlurListener);
    }
    this.content.setAttribute('part', 'region-content');
    this.element.appendChild(this.content);
    this.emit('content-changed');
  }
  public setOptions(
    options: Partial<
      Pick<
        RegionParams,
        | 'color'
        | 'start'
        | 'end'
        | 'drag'
        | 'content'
        | 'id'
        | 'resize'
        | 'resizeStart'
        | 'resizeEnd'
      >
    >
  ) {
    if (!this.element) return;
    if (options.color) {
      this.color = options.color;
    }
    if (options.drag !== undefined) {
      this.drag = options.drag;
      this.element.style.cursor = this.drag ? 'grab' : 'default';
    }
    if (options.start !== undefined || options.end !== undefined) {
      this.start = this.clampPosition(options.start ?? this.start);
      this.end = this.clampPosition(
        options.end ?? (this.isMarker() ? this.start : this.end)
      );
      this.renderPosition();
      this.setPart();
    }
    if (options.content) {
      this.setContent(options.content);
    }
    if (options.id) {
      this.id = options.id;
      this.setPart();
    }
    if (options.resize !== undefined && options.resize !== this.resize) {
      this.resize = options.resize;
      if (this.resize && !this.isMarker()) {
        this.addResizeHandles(this.element);
      } else {
        this.removeResizeHandles(this.element);
      }
    }
    if (options.resizeStart !== undefined) {
      this.resizeStart = options.resizeStart;
    }
    if (options.resizeEnd !== undefined) {
      this.resizeEnd = options.resizeEnd;
    }
  }
  public remove() {
    this.isRemoved = true;
    this.emit('remove');
    this.subscriptions.forEach((unsubscribe) => unsubscribe());
    if (this.element) {
      this.element.remove();
      this.element = null;
    }
  }
}
class RegionsPlugin extends BasePlugin<
  RegionsPluginEvents,
  RegionsPluginOptions
> {
  private regions: Region[] = [];
  private regionsContainer: HTMLElement;
  constructor(options?: RegionsPluginOptions) {
    super(options);
    this.regionsContainer = this.initRegionsContainer();
  }
  public static create(options?: RegionsPluginOptions) {
    return new RegionsPlugin(options);
  }
  onInit() {
    if (!this.arrwaveform) {
      throw Error('ArrWaveform is not initialized');
    }
    this.arrwaveform.getWrapper().appendChild(this.regionsContainer);
    this.subscriptions.push(
      this.arrwaveform.on('ready', (duration) => {
        this.regions.forEach((region) => region._setTotalDuration(duration));
      })
    );
    let activeRegions: Region[] = [];
    this.subscriptions.push(
      this.arrwaveform.on('timeupdate', (currentTime) => {
        const playedRegions = this.regions.filter(
          (region) =>
            region.start <= currentTime &&
            (region.end === region.start ? region.start + 0.05 : region.end) >=
            currentTime
        );
        playedRegions.forEach((region) => {
          if (!activeRegions.includes(region)) {
            this.emit('region-in', region);
          }
        });
        activeRegions.forEach((region) => {
          if (!playedRegions.includes(region)) {
            this.emit('region-out', region);
          }
        });
        activeRegions = playedRegions;
      })
    );
  }
  private initRegionsContainer(): HTMLElement {
    return createElement('div', {
      part: 'regions-container',
      style: {
        position: 'absolute',
        top: '0',
        left: '0',
        width: '100%',
        height: '100%',
        zIndex: '5',
        pointerEvents: 'none',
      },
    });
  }
  public getRegions(): Region[] {
    return this.regions;
  }
  private avoidOverlapping(region: Region) {
    if (!region.content) return;
    setTimeout(() => {
      const div = region.content as HTMLElement;
      const box = div.getBoundingClientRect();
      const overlap = this.regions
        .map((reg) => {
          if (reg === region || !reg.content) return 0;
          const otherBox = reg.content.getBoundingClientRect();
          if (
            box.left < otherBox.left + otherBox.width &&
            otherBox.left < box.left + box.width
          ) {
            return otherBox.height;
          }
          return 0;
        })
        .reduce((sum, val) => sum + val, 0);
      div.style.marginTop = `${overlap}px`;
    }, 10);
  }
  private adjustScroll(region: Region) {
    if (!region.element) return;
    const scrollContainer = this.arrwaveform?.getWrapper()?.parentElement;
    if (!scrollContainer) return;
    const {clientWidth, scrollWidth} = scrollContainer;
    if (scrollWidth <= clientWidth) return;
    const scrollBbox = scrollContainer.getBoundingClientRect();
    const bbox = region.element.getBoundingClientRect();
    const left = bbox.left - scrollBbox.left;
    const right = bbox.right - scrollBbox.left;
    if (left < 0) {
      scrollContainer.scrollLeft += left;
    } else if (right > clientWidth) {
      scrollContainer.scrollLeft += right - clientWidth;
    }
  }
  private virtualAppend(
    region: Region,
    container: HTMLElement,
    element: HTMLElement
  ) {
    const renderIfVisible = () => {
      if (!this.arrwaveform) return;
      const clientWidth = this.arrwaveform.getWidth();
      const scrollLeft = this.arrwaveform.getScroll();
      const scrollWidth = container.clientWidth;
      const duration = this.arrwaveform.getDuration();
      const start = Math.round((region.start / duration) * scrollWidth);
      const width =
        Math.round(((region.end - region.start) / duration) * scrollWidth) || 1;
      const isVisible =
        start + width > scrollLeft && start < scrollLeft + clientWidth;
      if (isVisible && !element.parentElement) {
        container.appendChild(element);
      } else if (!isVisible && element.parentElement) {
        element.remove();
      }
    };
    setTimeout(() => {
      if (!this.arrwaveform || !region.element) return;
      renderIfVisible();
      const unsubscribeScroll = this.arrwaveform.on('scroll', renderIfVisible);
      const unsubscribeZoom = this.arrwaveform.on('zoom', renderIfVisible);
      const unsubscribeResize = this.arrwaveform.on('resize', renderIfVisible);
      this.subscriptions.push(
        unsubscribeScroll,
        unsubscribeZoom,
        unsubscribeResize
      );
      region.once('remove', () => {
        unsubscribeScroll();
        unsubscribeZoom();
        unsubscribeResize();
      });
    }, 0);
  }
  private saveRegion(region: Region) {
    if (!region.element) return;
    this.virtualAppend(region, this.regionsContainer, region.element);
    this.avoidOverlapping(region);
    this.regions.push(region);
    const regionSubscriptions = [
      region.on('update', (side) => {
        if (!side) {
          this.adjustScroll(region);
        }
        this.emit('region-update', region, side);
      }),
      region.on('update-end', (side) => {
        this.avoidOverlapping(region);
        this.emit('region-updated', region, side);
      }),
      region.on('play', (end?: number) => {
        region.start !== region.end &&
          this.arrwaveform?.play(region.start, end);
      }),
      region.on('click', (e) => {
        this.emit('region-clicked', region, e);
      }),
      region.on('dblclick', (e) => {
        this.emit('region-double-clicked', region, e);
      }),
      region.on('content-changed', () => {
        this.emit('region-content-changed', region);
      }),
      region.once('remove', () => {
        regionSubscriptions.forEach((unsubscribe) => unsubscribe());
        this.regions = this.regions.filter((reg) => reg !== region);
        this.emit('region-removed', region);
      }),
    ];
    this.subscriptions.push(...regionSubscriptions);
    this.emit('region-created', region);
  }
  public addRegion(options: RegionParams): Region {
    if (!this.arrwaveform) {
      throw Error('ArrWaveform is not initialized');
    }
    const duration = this.arrwaveform.getDuration();
    const numberOfChannels =
      this.arrwaveform?.getDecodedData()?.numberOfChannels;
    const region = new SingleRegion(options, duration, numberOfChannels);
    this.emit('region-initialized', region);
    if (!duration) {
      this.subscriptions.push(
        this.arrwaveform.once('ready', (duration) => {
          region._setTotalDuration(duration);
          this.saveRegion(region);
        })
      );
    } else {
      this.saveRegion(region);
    }
    return region;
  }
  public enableDragSelection(
    options: Omit<RegionParams, 'start' | 'end'>,
    threshold = 3
  ): () => void {
    const wrapper = this.arrwaveform?.getWrapper();
    if (!wrapper || !(wrapper instanceof HTMLElement)) return () => undefined;
    const initialSize = 5;
    let region: Region | null = null;
    let startX = 0;
    let startTime = 0;
    const dragStream = createDragStream(wrapper, {threshold});
    const unsubscribe = effect(() => {
      const drag = dragStream.signal.value;
      if (!drag) return;
      if (drag.type === 'start') {
        startX = drag.x;
        if (!this.arrwaveform) return;
        const duration = this.arrwaveform.getDuration();
        const numberOfChannels =
          this.arrwaveform?.getDecodedData()?.numberOfChannels;
        const {width} = this.arrwaveform.getWrapper().getBoundingClientRect();
        startTime = (startX / width) * duration;
        const start = (drag.x / width) * duration;
        const end = ((drag.x + initialSize) / width) * duration;
        region = new SingleRegion(
          {...options, start, end},
          duration,
          numberOfChannels
        );
        this.emit('region-initialized', region);
        if (region.element) {
          this.regionsContainer.appendChild(region.element);
        }
      } else if (drag.type === 'move' && drag.deltaX !== undefined) {
        if (region) {
          region._onUpdate(
            drag.deltaX,
            drag.x > startX ? 'end' : 'start',
            startTime
          );
        }
      } else if (drag.type === 'end') {
        if (region) {
          this.saveRegion(region);
          region.updatingSide = undefined;
          region = null;
        }
      }
    }, [dragStream.signal]);
    return () => {
      unsubscribe();
      dragStream.cleanup();
    };
  }
  public clearRegions() {
    const regions = this.regions.slice();
    regions.forEach((region) => region.remove());
    this.regions = [];
  }
  public destroy() {
    this.clearRegions();
    super.destroy();
    this.regionsContainer.remove();
  }
}
export default RegionsPlugin;
export type Region = SingleRegion;
